import dayjs from "dayjs";
import { DataTypeSpecifier } from "verdi-common";
import { MasterIndex } from "verdi-web-client";
import { HistoricalDataBase } from "verdi-web-client/Models/HistoricalData/HistoricalDataBase";

import { ALL_DATA_TYPES } from "../../../services/data-types";
import { DISPLAY_DATETIME_FORMAT, SOURCE_TYPE, SourceTypeLabels, TIMESTAMP_COLUMN } from "../constants";
import { ColumnGrouping, Columns, PerDataKeyValues, Rows } from "../types";
import { getVerdiIdFromMasterIndex } from "./verdi-id";
import { generateDatabase, loadData } from "./db";
import { cascadingSort } from "./sort";
/**
 * Handles generating DB, loading data from the DB, and creating CSV data objects
 */
export async function createCSVData({
    sources,
    dataTypes,
    startDate,
    endDate,
    columnGroupKey,
    splitByPositionDepth,
}: {
    sources: { id: string; type: SOURCE_TYPE }[];
    dataTypes: DataTypeSpecifier[];
    startDate: Date;
    endDate: Date;
    columnGroupKey: ColumnGrouping;
    splitByPositionDepth: boolean;
}) {
    // Generate the database and load data
    const db = generateDatabase({ sources, dataTypes, splitByPositionDepth });
    db.compressedRequests = true;
    db.debugTiming = true;

    await loadData(db, startDate, endDate);

    // Create the data keys
    const { zoneKeys, deviceKeys, perDataKeyValues, validDataKeys, dataKeys, otherSourceKeys } = createDataKeys(
        db,
        splitByPositionDepth,
    );

    // Create the data rows
    const { rows, dataKeysToKeep } = createDataRows({
        db,
        perDataKeyValues,
        validDataKeys,
        dataKeys,
        splitByPositionDepth,
    });

    const zoneAndDeviceKeys = [...zoneKeys, ...deviceKeys];
    // Create the column order
    const { columns } = createColumns({
        zoneAndDeviceKeys,
        columnGroupKey,
        perDataKeyValues,
        dataKeysToKeep,
        otherSourceKeys,
    });

    return {
        rows,
        columns,
    };
}

/**
 * Create DataKeys
 */
function createDataKeys(db: HistoricalDataBase, splitByPositionDepth: boolean) {
    // Get the data keys from the database (unique column strings for source/dataType pairs)
    let dataKeys = Object.keys(db.dataKeyMetadataLookup);

    console.info(
        `DataExport: Parsing linked list with ${dataKeys.length} keys and ${db.data.value.length} data points`,
    );

    // Create an empty set to store the valid keys
    const validDataKeys = new Set() as Set<string>;

    // Create empty objects to store the source id, source type, source name, zone name, and data type for each key
    const sourceIDsPerDataKey: Record<string, string> = {};
    const sourceTypesPerDataKey: Record<string, string> = {};
    const sourceNamesPerDataKey: Record<string, string> = {};
    const sourceZoneNamesPerDataKey: Record<string, string> = {};
    const dataTypesPerDataKeys: Record<string, string> = {};
    const sourceVerdiIdsPerDataKey: Record<string, string> = {};
    const sourceFieldNamesPerDataKey: Record<string, string> = {};
    const latitudePerDataKey: Record<string, number> = {};
    const longitudePerDataKey: Record<string, number> = {};
    const depthPerDataKey: Record<string, number> = {};

    // Create empty arrays to store the zone, device, and other keys
    const zoneKeys: string[] = [];
    const deviceKeys: string[] = [];
    const otherSourceKeys: string[] = [];

    // Iterate through the data keys
    dataKeys.forEach((k) => {
        // Get the source and zone info for the data key
        const sourceID = db.dataKeyMetadataLookup[k].source;
        let sourceName;
        let sourceZoneName;
        let sourceFieldName;

        // Optionally get the position depth info for the data key
        if (splitByPositionDepth) {
            const position = db.dataKeyMetadataLookup[k]?.position;
            if (position) {
                latitudePerDataKey[k] = position.lat ?? 0;
                longitudePerDataKey[k] = position.lng ?? 0;
                // @ts-ignore position.depth does exist, but missing from type def
                depthPerDataKey[k] = position.depth;
            }
        }

        // Set the source ID
        sourceIDsPerDataKey[k] = sourceID;

        // Add the key to the appropriate array and set the name and zone name

        // Zone source
        if (MasterIndex.zone.byID[sourceID]) {
            // Set the source and zone names
            sourceName = MasterIndex.zone.byID[sourceID].name;
            sourceZoneName = sourceName;

            // Set the source type
            sourceTypesPerDataKey[k] = SourceTypeLabels[SOURCE_TYPE.ZONE];

            // Add the key to the zone rows
            zoneKeys.push(k);

            // Set the source field name
            sourceFieldName = MasterIndex.zone.byID[sourceID]?.linkedAreaOfInterest?.name || "";

            // Irrigation device source
        } else if (MasterIndex.irrigationDevice.byID[sourceID]) {
            // "moisture" dataType is invalid for irrigation devices
            if (db.dataKeyMetadataLookup[k].dataType === "moisture") {
                console.warn(`DataExport: Invalid column. dataType of "moisture" found for irrigation device: ${k}`);
                return;
            }

            // Add the key to the device rows
            deviceKeys.push(k);

            // Set the source name
            sourceName = MasterIndex.irrigationDevice.byID[sourceID].name;

            // Set the source type
            sourceTypesPerDataKey[k] = SourceTypeLabels[SOURCE_TYPE.DEVICE];

            // Set the zone name if there are zones attached
            if (MasterIndex.irrigationDevice.byID[sourceID].zonesByValve) {
                // Combine the names of all the zones attached to the device (filter out duplicates caused by single-valve devices)
                sourceZoneName = [
                    ...new Set(
                        MasterIndex.irrigationDevice.byID[sourceID].zonesByValve
                            .filter((z) => z !== null && z !== undefined)
                            .map((z) => z.name),
                    ),
                ].join(" | ");
            }

            // Set the verdiId
            sourceVerdiIdsPerDataKey[k] = getVerdiIdFromMasterIndex(sourceID) ?? "";

            // Set the source field name
            sourceFieldName = MasterIndex.irrigationDevice.byID[sourceID]?.linkedAreaOfInterest?.name || "";

            // Block valve source
        } else if (MasterIndex.blockValve.byID[sourceID]) {
            // "moisture" dataType is invalid for block valves
            if (db.dataKeyMetadataLookup[k].dataType === "moisture") {
                console.warn(`DataExport: Invalid column. dataType of "moisture" found for block valve: ${k}`);
                return;
            }

            // Add the key to the device rows
            deviceKeys.push(k);

            // Set the name
            sourceName = MasterIndex.blockValve.byID[sourceID].name;

            // Set the source type
            sourceTypesPerDataKey[k] = SourceTypeLabels[SOURCE_TYPE.DEVICE];

            try {
                // Set the zone name if there are zones attached
                if (MasterIndex.blockValve.byID[sourceID].connectedZones) {
                    // Combine the names of all the zones attached to the device
                    sourceZoneName = [
                        ...new Set(
                            MasterIndex.blockValve.byID[sourceID].connectedZones
                                .filter((z) => z !== null && z !== undefined)
                                .map((z) => z.name),
                        ),
                    ].join(" | ");
                }
            } catch (e) {
                console.warn(`Failed to find name for connected zone for device ${sourceID}`);
                sourceZoneName = "Unknown Zone";
            }

            // Set the verdiId
            sourceVerdiIdsPerDataKey[k] = getVerdiIdFromMasterIndex(sourceID) ?? "";

            // Set the source field name
            sourceFieldName = MasterIndex.blockValve.byID[sourceID]?.linkedAreaOfInterest?.name || "";

            // Third party device source
        } else if (MasterIndex.thirdPartyDevice.byID[sourceID]) {
            // Add the key to the device rows
            deviceKeys.push(k);

            // Set the name
            sourceName = MasterIndex.thirdPartyDevice.byID[sourceID].name;
            // Set the source type
            sourceTypesPerDataKey[k] = SourceTypeLabels[SOURCE_TYPE.DEVICE];

            // Set the zone name if there are zones attached
            if (MasterIndex.thirdPartyDevice.byID[sourceID].connectedZones) {
                // Combine the names of all the zones attached to the device
                sourceZoneName = MasterIndex.thirdPartyDevice.byID[sourceID].connectedZones
                    .filter((z) => z !== null && z !== undefined)
                    .map((z) => z.name)
                    .join(" | ");
            }

            // Set the verdiId
            sourceVerdiIdsPerDataKey[k] = getVerdiIdFromMasterIndex(sourceID) ?? "";

            // Set the source field name
            sourceFieldName = MasterIndex.thirdPartyDevice.byID[sourceID]?.linkedAreaOfInterest?.name || "";

            // Other source with metadata available
        } else if (db.dataKeyMetadataLookup[k]) {
            // Add the key to the other rows
            otherSourceKeys.push(k);

            // Set the name
            sourceName = db.dataKeyMetadataLookup[k].source;

            // Set the zone name if there are zones attached
            if (db.dataKeyMetadataLookup[k].relevantZones) {
                // Combine the names of all the zones attached to the device
                sourceZoneName = db.dataKeyMetadataLookup[k].relevantZones
                    ?.map((zid) => MasterIndex.zone.byID[zid].name)
                    .join(" | ");
            }

            // Other source
        } else if (db.dataKeys[k]?.relevantZones) {
            // Add the key to the other rows
            otherSourceKeys.push(k);

            // Set the name and zone name
            sourceName = "unknown name";
            sourceZoneName = "unknown zone";
            sourceFieldName = "unknown field";
        } else {
            // Add the key to the other rows
            otherSourceKeys.push(k);

            // Set the name and zone name
            sourceName = "unknown name";
            sourceZoneName = "unknown zone";
            sourceFieldName = "unknown field";
        }

        // Set source name to empty string if it's the same as the ID (issue with device names being set to the ID if they're not set)
        if (sourceName === sourceID) {
            sourceName = "";
        }

        // Key is valid at this point
        validDataKeys.add(k);

        // Add the source name, zone name, and data type to the appropriate objects
        sourceZoneNamesPerDataKey[k] = sourceZoneName as string;
        sourceNamesPerDataKey[k] = sourceName as string;
        sourceFieldNamesPerDataKey[k] = sourceFieldName as string;
        dataTypesPerDataKeys[k] = ALL_DATA_TYPES[db.dataKeyMetadataLookup[k].dataType]?.name ?? "N/A";
    });

    // Add the valid keys to an array
    dataKeys = [...validDataKeys] as string[];

    // Return compiled keys
    return {
        zoneKeys,
        deviceKeys,
        perDataKeyValues: {
            sourceIDsPerDataKey,
            sourceTypesPerDataKey,
            sourceNamesPerDataKey,
            dataTypesPerDataKeys,
            sourceZoneNamesPerDataKey,
            sourceVerdiIdsPerDataKey,
            sourceFieldNamesPerDataKey,
            latitudePerDataKey,
            longitudePerDataKey,
            depthPerDataKey,
        } as PerDataKeyValues,
        validDataKeys,
        otherSourceKeys,
        dataKeys,
    };
}

/**
 * Create the data rows for the CSV
 */
function createDataRows({
    db,
    perDataKeyValues,
    validDataKeys,
    dataKeys,
    splitByPositionDepth = false,
}: {
    db: HistoricalDataBase;
    perDataKeyValues: PerDataKeyValues;
    validDataKeys: Set<string>;
    dataKeys: string[];
    splitByPositionDepth?: boolean;
}) {
    const {
        sourceIDsPerDataKey,
        sourceTypesPerDataKey,
        sourceNamesPerDataKey,
        dataTypesPerDataKeys,
        sourceZoneNamesPerDataKey,
        sourceVerdiIdsPerDataKey,
        sourceFieldNamesPerDataKey,
        latitudePerDataKey,
        longitudePerDataKey,
        depthPerDataKey,
    } = perDataKeyValues;

    // Add the row labels (in the first column along with the ISO datetimes)
    sourceIDsPerDataKey[TIMESTAMP_COLUMN] = "Source ID";
    sourceTypesPerDataKey[TIMESTAMP_COLUMN] = "Source type";
    sourceNamesPerDataKey[TIMESTAMP_COLUMN] = "Source name";
    dataTypesPerDataKeys[TIMESTAMP_COLUMN] = "Data type";
    sourceZoneNamesPerDataKey[TIMESTAMP_COLUMN] = "Source zone name";
    sourceVerdiIdsPerDataKey[TIMESTAMP_COLUMN] = "Verdi ID";
    sourceFieldNamesPerDataKey[TIMESTAMP_COLUMN] = "Source field name";

    // Optionally add row labels for position depth columns
    if (splitByPositionDepth) {
        depthPerDataKey[TIMESTAMP_COLUMN] = "Depth";
        latitudePerDataKey[TIMESTAMP_COLUMN] = "Latitude";
        longitudePerDataKey[TIMESTAMP_COLUMN] = "Longitude";
    }

    // Create an empty array to store the data rows
    const dataRows: Rows = [];

    // Create a set to store the keys with non-zero values
    const keysWithNonZeroValues = new Set();

    // Create an empty object to store the most recent value for each key
    const mostRecentValuesForKeys: Record<string, any> = {};

    // Iterate through the data points in the database
    db.data.value.forEach((dataPoint) => {
        // Create a copy of the data point's data
        const dataRow: Record<string, any> = { ...dataPoint.data };

        // Get datetime from the data point
        const dateTime = dataPoint.date;

        // Iterate through the data keys
        dataKeys.forEach((k) => {
            // If the key exists in the data point's data, its value is now the most recent value for the key
            if (dataRow[k] !== null && dataRow[k] !== undefined) {
                // If the key's value is non-zero, add it to the set of keys with non-zero values
                if (dataRow[k] !== 0) {
                    keysWithNonZeroValues.add(k);
                }

                // Set the most recent value for the key
                mostRecentValuesForKeys[k] = dataRow[k];

                // If the key does not exist in the data point's data, set its value to the most recent value for the key
            } else if (mostRecentValuesForKeys[k] !== null && mostRecentValuesForKeys[k] !== undefined) {
                dataRow[k] = mostRecentValuesForKeys[k];
            } else {
                dataRow[k] = null;
            }
        });

        // Add the local date and time to the data
        dataRow[TIMESTAMP_COLUMN] = dayjs(dateTime).format(DISPLAY_DATETIME_FORMAT);

        // Add the data row to the array of data rows
        dataRows.push(dataRow);
    });

    // Keep the keys with non-zero values and the valid keys
    const dataKeysToKeep = new Set([...validDataKeys, ...keysWithNonZeroValues, TIMESTAMP_COLUMN]) as Set<string>;

    // Remove dataRows with keys not in the set of keys to keep
    dataRows.forEach((dataRow) => {
        Object.keys(dataRow).forEach((rowKey) => {
            if (!dataKeysToKeep.has(rowKey)) {
                delete dataRow[rowKey];
            }
        });
    });

    // Combine all the rows together
    const rows: Rows = [
        sourceTypesPerDataKey,
        sourceNamesPerDataKey,
        sourceIDsPerDataKey,
        sourceVerdiIdsPerDataKey,
        sourceZoneNamesPerDataKey,
        sourceFieldNamesPerDataKey,
        dataTypesPerDataKeys,
    ];

    // Add position depth columns if requested
    if (splitByPositionDepth) {
        rows.push(latitudePerDataKey, longitudePerDataKey, depthPerDataKey);
    }

    // Add the data rows to the rows individually to avoid a max call stack error
    dataRows.forEach((row) => rows.push(row));

    return { rows, dataKeysToKeep };
}

/**
 * Create the column order for the CSV
 */
function createColumns({
    zoneAndDeviceKeys,
    columnGroupKey,
    perDataKeyValues,
    dataKeysToKeep,
    otherSourceKeys,
}: {
    zoneAndDeviceKeys: string[];
    columnGroupKey: ColumnGrouping;
    perDataKeyValues: PerDataKeyValues;
    dataKeysToKeep: Set<string>;
    otherSourceKeys: string[];
}) {
    const sorted = cascadingSort({
        primarySortKey: columnGroupKey,
        keys: zoneAndDeviceKeys,
        perDataKeyValues,
    });

    // Create an array to store the column headers
    let columns: Columns = [TIMESTAMP_COLUMN];

    // Add the merged keys and other keys to the column headers
    columns.push(...sorted, ...otherSourceKeys);

    // Remove columns with keys not in the set of keys to keep
    columns = columns.filter((columnKey) => dataKeysToKeep.has(columnKey));

    return { columns };
}
